import { Stack } from '@mantine/core';
import { memo } from 'react';
import {
  isChatGroup,
  isEncounterGroup,
  isFaxGroup,
  isLabOrderGroup,
  isLabOrderResultsGroup,
  isPrescriptionOrderGroup,
  isSmsGroup,
  isStandaloneNoteGroup,
  isCiIntroCallGroup,
  isWarmHandoffGroup,
  isVoiceGroup,
  isAppointmentGroup,
  isEligibilityFormGroup,
  isPaymentEventGroup,
  isBenefitEventGroup,
  isPaymentOrderGroup,
  isRefundEventGroup,
  isTaskCompletedGroup,
  TimelineGroup,
} from './types';
import { PaymentOrderCard } from './PaymentOrderCard';
import { TaskCompletedTimelineItem } from './TimelineItems/TaskCompletedTimelineItem';
import { PaymentEventCard } from './PaymentEventCard';
import { BenefitEventCard } from './BenefitEventCard';
import { RefundEventCard } from './RefundEventCard';
import { PhoneCallTimelineItem } from './TimelineItems/PhoneCallTimelineItem';
import { SmsTimelineItem } from './TimelineItems/SmsTimelineItem';
import { StandaloneNoteTimelineItem } from './TimelineItems/StandaloneNoteTimelineItem';
import { PacIntroCallTimelineItem } from './TimelineItems/PatientAccessCall/PacIntroCallTimelineItem';
import { WarmHandoffTimelineItem } from './TimelineItems/PatientAccessCall/WarmHandoffTimelineItem';
import { PrescriptionOrderTimelineItem } from '@/components/Timeline/TimelineItems/PrescriptionOrderTimelineItem';
import { ChatTimelineItem } from '@/components/Timeline/TimelineItems/ChatTimelineItem';
import { DiagnosticReportTimelineItem } from '@/components/Timeline/TimelineItems/DiagnosticReportTimelineItem';
import { LabOrderTimelineItem } from '@/components/Timeline/TimelineItems/LabOrderTimelineItem';
import { LazyTimelineItem } from '@/components/Timeline/TimelineItems/LazyTimelineItem';
import { EncounterTimelineItem } from '@/components/Timeline/TimelineItems/EncounterTimelineItem';
import { AppointmentTimelineItem } from '@/components/Timeline/TimelineItems/AppointmentTimelineItem';
import { TimelineItemWithIcon } from '@/components/Timeline/TimelineItems/TimelineItemWithIcon';
import { FaxTimelineItem } from '@/components/Timeline/TimelineItems/FaxTimelineItem';
import { EligibilityFormTimelineItem } from '@/components/Timeline/TimelineItems/EligibilityFormTimelineItem';
import { Appointment, Coding } from '@medplum/fhirtypes';
import { getReferenceString } from '@medplum/core';
import classes from './clipped.module.css';

interface TimelineItemsProps {
  timelineGroups: TimelineGroup[];
  activityCodeSystem?: Coding[];
  highlightRef: string | null;
  copyDirectLink: (itemReference: string) => Promise<void>;
  hasActiveFilters: boolean;
  handleNavigateToItem: (itemReference: string) => void;
  searchTerm?: string;
  onAppointmentStatusChange?: (updatedAppointment: Appointment) => void;
}

interface RenderOptions {
  highlightRef: string | null;
  copyDirectLink: (itemReference: string) => Promise<void>;
  hasActiveFilters: boolean;
  handleNavigateToItem: (itemReference: string) => void;
  searchTerm?: string;
  onAppointmentStatusChange?: (updatedAppointment: Appointment) => void;
  activityCodeSystem?: Coding[];
}

function TimelineItemSelector({ group, ...options }: { group: TimelineGroup } & RenderOptions) {
  if (isVoiceGroup(group)) {
    return <PhoneCallTimelineItem item={group.items[0]} notes={group.notes} {...options} />;
  }

  if (isChatGroup(group)) {
    return <ChatTimelineItem group={group} notes={group.notes} {...options} />;
  }

  if (isSmsGroup(group)) {
    return <SmsTimelineItem group={group} notes={group.notes} {...options} />;
  }

  if (isFaxGroup(group)) {
    return <FaxTimelineItem group={group} notes={group.notes} {...options} />;
  }

  if (isStandaloneNoteGroup(group)) {
    return <StandaloneNoteTimelineItem group={group} {...options} />;
  }

  if (isTaskCompletedGroup(group)) {
    return <TaskCompletedTimelineItem group={group} />;
  }

  if (isCiIntroCallGroup(group)) {
    return (
      <PacIntroCallTimelineItem
        group={group}
        highlightRef={options.highlightRef}
        copyDirectLink={options.copyDirectLink}
      />
    );
  }

  if (isWarmHandoffGroup(group)) {
    return (
      <WarmHandoffTimelineItem
        group={group}
        highlightRef={options.highlightRef}
        copyDirectLink={options.copyDirectLink}
      />
    );
  }

  if (isLabOrderGroup(group)) {
    return <LabOrderTimelineItem group={group} {...options} />;
  }

  if (isLabOrderResultsGroup(group)) {
    return <DiagnosticReportTimelineItem group={group} {...options} />;
  }

  if (isEncounterGroup(group)) {
    return <EncounterTimelineItem group={group} {...options} />;
  }

  if (isPrescriptionOrderGroup(group)) {
    return <PrescriptionOrderTimelineItem group={group} {...options} />;
  }

  if (isAppointmentGroup(group)) {
    const { onAppointmentStatusChange, activityCodeSystem, ...restOptions } = options;
    return (
      <AppointmentTimelineItem
        group={group}
        activityCodeSystem={activityCodeSystem ?? []}
        {...restOptions}
        onStatusChange={onAppointmentStatusChange}
      />
    );
  }

  if (isEligibilityFormGroup(group)) {
    return <EligibilityFormTimelineItem group={group} highlightRef={options.highlightRef} />;
  }

  // One open payment order per patient, so the group is a single item — the same
  // shape isVoiceGroup uses. No highlightRef: shouldForceRender keys on
  // getReferenceString, which is undefined for a payment item, so a payment card
  // is never a deep-link target.
  if (isPaymentOrderGroup(group)) {
    return <PaymentOrderCard item={group.items[0]} />;
  }

  // One event per group — cards 02 and 03 are never coalesced.
  if (isPaymentEventGroup(group)) {
    return <PaymentEventCard item={group.items[0]} />;
  }

  // One event per group — append-only, never coalesced.
  if (isBenefitEventGroup(group)) {
    return <BenefitEventCard item={group.items[0]} />;
  }

  if (isRefundEventGroup(group)) {
    return <RefundEventCard item={group.items[0]} />;
  }

  return null;
}

function shouldForceRender(highlightRef: string | null, group: TimelineGroup) {
  if (!highlightRef) return false;
  return group.items.some((i) => getReferenceString(i.reference) === highlightRef);
}

export const TimelineItems = memo((props: TimelineItemsProps) => {
  const { timelineGroups, ...options } = props;

  return (
    // classes.clipped, not overflow: hidden — it clips the spine line
    // TimelineItemWithIcon draws past the last item without making this box a
    // silent scroll container for scrollIntoView to displace. Measured on a
    // real deep link before the change: scrollTop 67 on this element, which is
    // what put the focused card's header under the sticky filter bar with no
    // way to scroll it back.
    <Stack px="md" pb="md" className={classes.clipped}>
      {timelineGroups.map((group, index) => (
        <LazyTimelineItem
          key={group.id}
          fallbackHeight={120}
          forceRender={shouldForceRender(options.highlightRef, group)}
        >
          <TimelineItemWithIcon group={group} isLast={index === timelineGroups.length - 1}>
            <TimelineItemSelector group={group} {...options} />
          </TimelineItemWithIcon>
        </LazyTimelineItem>
      ))}
    </Stack>
  );
});
